import getUrlParam, { decodeUrlQueryValue } from 'components/Dashboard/utilities/getUrlParam';
import { FilterPartyNames } from 'components/Dashboard/Analytics/ui/AnalyticsFilters/PartyMetadata';
import { useContext } from 'react';
import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';
import { store } from 'components/Store';
import { chartDataOptions } from './createAnalyticsState';
import { useLocation } from 'react-router';
import dayjs from 'dayjs';
import useSelectedCountriesFilters from './useSelectedCountriesFilters';
import { useChartDataOptionSelected } from './ui/MentionsByTopic/ChartDataTable';
import ChangeSearchQuery from 'components/Dashboard/utilities/changeSearchQuery';
import getAppSite from 'utils/getAppSite';
import utc from 'dayjs/plugin/utc';
import graphDataSections from 'components/Dashboard/Analytics/graphDataSections';
import useAnalyticsApiHelpers from 'components/Dashboard/Analytics/hooks/useAnalyticsApiHelpers';
dayjs.extend(utc);

const useRightPanelIndividualCall = (props) => {
  const { analyticsState, contentState } = props;
  const { dataKey, chartDataKeys, date, region, totalRow } = contentState;

  const { currentView, allowFrontbenchers } = analyticsState;
  const globalState = useContext(store);
  const { state } = globalState;
  const { keywordsLists } = state;

  const { parliamentaryPositionsFilters } = useAnalyticsApiHelpers();
  const list = keywordsLists.find((item) => item.id === parseInt(getUrlParam('topic-id')));

  const location = useLocation();
  const InterestByRegionSection = location.pathname.includes('/analytics/interest-by-region');
  const { chartOptionName: chartDataOptionNameState } = useChartDataOptionSelected(analyticsState);
  const chartOptionName = InterestByRegionSection ? 'Region' : chartDataOptionNameState;

  const totalCell = dataKey === 'totalCell';
  const contributionGraph = chartOptionName === 'Contribution type' || chartOptionName === 'Overall';
  const generalPageSize = parseInt(import.meta.env.VITE_APP_PAGE_SIZE);

  const { generalApiCall } = useGeneralApiCall();
  const {
    getCountrySpecificFilters,
    getWrittenQuestionCountryFilters,
    UKSelected,
    WalesSelected,
    ScotlandSelected,
    NorthernIrelandSelected,
  } = useSelectedCountriesFilters();
  const usaSite = getAppSite() === 'usa';

  // Helper function to remove duplicate filters
  const deduplicateFilters = (filters) => {
    const seen = new Map();
    return filters.filter((filter) => {
      const key = `${filter.field}:${filter.value}:${filter.operator}`;
      if (seen.has(key)) {
        return false;
      }
      seen.set(key, true);
      return true;
    });
  };

  // InterestByRegion passes its own raw content-type labels (see useCallInterestByRegion's
  // `types` array) instead of graphDataSections' display names - alias the two that differ so
  // the lookup below still finds the section and applies its contentType filter.
  const interestByRegionNameAliases = {
    'Parliamentary contribution': 'Spoken parliamentary contributions',
    EDM: 'EDMs',
    Tweet: 'Tweets',
    Retweet: 'Retweets',
  };

  const sectionContentTypeValues = ({ type }) => {
    // Written questions are queried by questionByContactId (the MP who asked it) rather than
    // contactIds, which also matches answering ministers and returns the wrong sentiment mix.
    const sectionItem = graphDataSections.find((item) => item.name === (interestByRegionNameAliases[type] ?? type));
    const resolvedType = sectionItem?.type ?? type;
    const isWrittenQuestion = resolvedType === 'WrittenQuestion' || type?.startsWith('Written questions');
    let typeToCall;
    let contentTypeField;
    if (isWrittenQuestion) {
      typeToCall = 'WrittenQuestion';
      contentTypeField = 'questionByContactId';
    } else if (type?.toLowerCase()?.includes('tweet')) {
      typeToCall = 'tweet';
      contentTypeField = 'contactId';
    } else {
      typeToCall = 'ParliamentaryRecord';
      contentTypeField = 'contactIds';
    }
    return { typeToCall, contentTypeField, isWrittenQuestion, sectionItem };
  };

  const individualApiCall = async ({
    type,
    sentiment,
    pageNumber,
    snapshot,
    source,
    freeSearch,
    stakeholderId,
    pageSize,
    dateFilters,
  }) => {
    const pathname = `/api/search/${freeSearch ? 'query' : 'query-by-keyword-list'}`;
    const method = 'post';
    const { typeToCall, contentTypeField, sectionItem } = sectionContentTypeValues({ type });

    if ((type === 'Edm' && !UKSelected) || (type === 'SeneddCommitteeTranscriptContribution' && !WalesSelected)) {
      return {};
    }

    let requestProperties = {
      pageSize: pageSize ?? generalPageSize,
      pageNumber: pageNumber ?? 1,
      type: typeToCall,
      includeHighlights: true,
      sort: { field: 'dateTime', order: 'DESC' },
    };

    if (freeSearch) {
      requestProperties = {
        ...requestProperties,
        query: ChangeSearchQuery(decodeUrlQueryValue(freeSearch)),
      };
    } else {
      requestProperties = {
        ...requestProperties,
        keywordListId: list?.id,
      };
    }

    let chartDataOption = chartDataOptions.find((option) => option.name === chartOptionName);
    let filters = [];

    if (type === 'Committee contributions') {
      filters = [
        ...filters,
        {
          field: 'contentType',
          value: 'SeneddCommitteeTranscriptContribution',
          operator: 'str_eq',
        },
      ];
    }

    if (sentiment) {
      filters.push({
        field: 'sentiment',
        value: sentiment,
        operator: 'str_eq',
      });
    }

    if (stakeholderId) {
      filters = [
        ...filters,
        {
          field: contentTypeField,
          value: stakeholderId,
          operator: 'str_eq',
        },
      ];
    }

    // additionalFilters (TweetsFilters) restricts tweets to a handful of policyMogulUserType
    // values (MPs/Lords), excluding MSPs/MSs. The InterestByRegion table is built without this
    // restriction (see useCallInterestByRegion's `types`), so applying it here would show a
    // panel count lower than the table for Scotland/Wales tweets - skip it on this page to keep
    // the drill-down consistent with the table it's drilling into.
    if (sectionItem?.additionalFilters && !InterestByRegionSection) {
      filters = [...filters, ...sectionItem.additionalFilters];
    }

    if (getAppSite() === 'global' && typeToCall === 'WrittenQuestion' && !stakeholderId) {
      filters = [...filters, ...getWrittenQuestionCountryFilters()];
    } else if (getAppSite() === 'global' && sectionItem) {
      filters = [...filters, ...getCountrySpecificFilters({ value: sectionItem?.value, field: sectionItem?.field })];
    }
    if (getAppSite() === 'usa' && typeToCall.toLowerCase() !== 'tweet') {
      // Only add CREC filter for non-tweet types on US site
      filters = [
        ...filters,
        {
          field: 'contentType',
          value: 'CREC',
          operator: 'str_eq',
        },
      ];
    }
    if (totalCell && !InterestByRegionSection) {
      if (!stakeholderId && !usaSite) {
        filters = [...filters, ...parliamentaryPositionsFilters];
      }
      if ((chartOptionName === 'Party' || chartOptionName === 'Region') && !!chartDataKeys) {
        chartDataKeys.forEach((key) => {
          let partyOption = chartOptionName === 'Party';
          filters.push({
            field: partyOption ? 'contactParty' : 'contactRegion',
            value: partyOption && FilterPartyNames[key] ? FilterPartyNames[key] : key,
            operator: 'str_eq',
          });
        });
      }
    }
    if (totalCell && InterestByRegionSection) {
      if (!!chartDataKeys) {
        if (totalRow) {
          const value = region === 'Yorkshire and the Humber' ? 'yorkshire & the humber' : region;
          filters.push({
            field: 'contactRegion',
            value: value,
            operator: 'str_eq',
          });
        } else {
          chartDataKeys.forEach((key) => {
            const region = key === 'Yorkshire and the Humber' ? 'yorkshire & the humber' : key;
            filters.push({
              field: 'contactRegion',
              value: region,
              operator: 'str_eq',
            });
          });
        }
      }
    }

    if (typeToCall.toLowerCase() === 'tweet' && sectionItem?.field === 'isRetweet') {
      filters.push({
        field: 'isRetweet',
        value: sectionItem.value,
        operator: 'str_eq',
      });
    }

    if (!totalCell && !contributionGraph && !stakeholderId) {
      const changeDataKey = () => {
        let key = dataKey;
        if (chartOptionName === 'Party' && FilterPartyNames[dataKey]) {
          key = FilterPartyNames[dataKey];
        }
        if (chartOptionName === 'Parliamentary position') {
          if (dataKey === 'Westminster Backbenchers') {
            key = 'backbencher';
          } else if (dataKey === 'Westminster Government') {
            key = 'government';
          } else if (dataKey === 'MSs') {
            key = 'ms';
          } else if (dataKey === 'MSPs') {
            key = 'msp';
          } else if (dataKey === 'MLAs') {
            key = 'mla';
          } else {
            key = dataKey.toLowerCase();
          }
        }

        if (chartOptionName === 'Region' && dataKey === 'Yorkshire and the Humber') {
          key = 'yorkshire & the humber';
        }

        if (InterestByRegionSection) {
          key = region === 'Yorkshire and the Humber' ? 'yorkshire & the humber' : region;
        }
        return key;
      };
      filters.push({
        field: `${chartDataOption.bucketField}`,
        value: changeDataKey(),
        operator: 'str_eq',
      });
    }

    if (InterestByRegionSection) {
      // A row for a specific devolved region should include that region's own position category even when
      // the corresponding nation checkbox isn't ticked - clicking Scotland's row should show MSPs regardless.
      const scotlandIncluded = ScotlandSelected || region === 'Scotland';
      const walesIncluded = WalesSelected || region === 'Wales';
      const northernIrelandIncluded = NorthernIrelandSelected || region === 'Northern Ireland';
      if (!allowFrontbenchers) {
        filters = [
          ...filters,
          ...(UKSelected ? [{ field: 'contactPositionCategories', value: 'Backbencher', operator: 'str_eq' }] : []),
          ...(scotlandIncluded ? [{ field: 'contactPositionCategories', value: 'MSP', operator: 'str_eq' }] : []),
          ...(walesIncluded ? [{ field: 'contactPositionCategories', value: 'MS', operator: 'str_eq' }] : []),
          ...(northernIrelandIncluded
            ? [{ field: 'contactPositionCategories', value: 'MLA', operator: 'str_eq' }]
            : []),
        ];
      } else {
        filters = [
          ...filters,
          ...(scotlandIncluded ? [{ field: 'contactPositionCategories', value: 'MSP', operator: 'str_eq' }] : []),
          ...(walesIncluded ? [{ field: 'contactPositionCategories', value: 'MS', operator: 'str_eq' }] : []),
          ...(northernIrelandIncluded
            ? [{ field: 'contactPositionCategories', value: 'MLA', operator: 'str_eq' }]
            : []),
          ...(UKSelected ? [{ field: 'contactPositionCategories', value: 'Backbencher', operator: 'str_eq' }] : []),
          { field: 'contactPositionCategories', value: 'Shadow Government', operator: 'str_eq' },
          { field: 'contactPositionCategories', value: 'Government', operator: 'str_eq' },
          { field: 'contactPositionCategories', value: 'Shadow Cabinet', operator: 'str_eq' },
          { field: 'contactPositionCategories', value: 'Cabinet', operator: 'str_eq' },
        ];
      }
    } else if (!totalCell && chartOptionName !== 'Parliamentary position' && !stakeholderId && !usaSite) {
      filters = [...filters, ...parliamentaryPositionsFilters];
    }

    //DATE FILTER
    if (snapshot) {
      if (chartOptionName !== 'Parliamentary position' && !stakeholderId && !usaSite) {
        filters = [...filters, ...parliamentaryPositionsFilters];
      }
      if (dateFilters) {
        filters = [...filters, ...dateFilters];
      } else {
        filters.push(
          {
            field: 'dateTime',
            value: `${dayjs()
              .subtract(import.meta.env.VITE_APP_SENTIMENT_SNAPSHOT_NUM_DAYS ?? 90, 'day')
              .startOf('day')
              .format('YYYY-MM-DD')}`,
            operator: 'DATE_GTE',
          },
          {
            field: 'dateTime',
            value: `${dayjs().format('YYYY-MM-DD')}`,
            operator: 'DATE_LT',
          }
        );
      }
    } else if (InterestByRegionSection) {
      filters.push(
        {
          field: 'dateTime',
          value: dayjs().subtract(365, 'day').add(-1, 'day').startOf('day').format('YYYY-MM-DD'),
          operator: 'date_gte',
        },
        {
          field: 'dateTime',
          value: dayjs().add('1', 'day').format('YYYY-MM-DD'),
          operator: 'date_lt',
        }
      );
    } else {
      let nextDate = dayjs(date).startOf('day').add('1', currentView.toLowerCase()).format('YYYY-MM-DD');
      filters.push(
        {
          field: 'dateTime',
          value: `${dayjs(date).startOf('day').format('YYYY-MM-DD')}`,
          operator: 'DATE_GTE',
        },
        { field: 'dateTime', value: `${nextDate}`, operator: 'DATE_LT' }
      );
    }
    // Remove duplicate filters before making the API call
    filters = deduplicateFilters(filters);

    requestProperties = {
      ...requestProperties,
      filters,
    };

    return await generalApiCall({
      pathname,
      method,
      requestProperties,
      requestSource: source,
      notShowErrorMessage: true,
      bigInt: true,
      needsAuthentication: true,
    });
  };

  return { individualApiCall };
};

export default useRightPanelIndividualCall;
